import Debounce from '@sd-utility/debounce';
import { IconChevronRight } from '@tvg/fuse-icons';
import PropTypes from 'prop-types';
import React, {
	useCallback,
	useEffect,
	useLayoutEffect,
	useMemo,
	useRef
} from 'react'
import { Button, SpanButton } from '../../fuse-buttons/src/buttons';
import { Picture } from '../../fuse-picture/src/picture';
import {
	StyledButtons,
	StyledContent,
	StyledContentBanner,
	StyledHeading,
	StyledParagraph,
	StyledPicture,
	StyledRepresentative,
	StyledSubHeading,
	StyledText,
} from './styled-content-banner-1';

const defaultProps = {
	className: undefined,
	dataAnalyticsId: undefined,
	dataAnalyticsIdSecondary: undefined,
	dataTestId: undefined,
	image: {
		fallbackSource: undefined,
		isLazy: true,
		sources: {
			srcSet: undefined,
			type: undefined,
		},
		size: 'small',
	},
	link: {
		href: undefined,
		onClick: undefined,
		text: undefined,
	},
	lLayout: 'v1',
	secondaryLink: {
		href: undefined,
		onClick: undefined,
		text: undefined,
	},
	smallVariant: false,
	textContent: {
		addBrandColour: undefined,
		heading: undefined,
		headingTag: 'h2',
		legalText: undefined,
		paragraph: undefined,
		representative: undefined,
		subHeading: undefined,
		subHeadingTag: 'h3',
		switchSubHeadingPosition: false,
	},
	variant: 'brandLight',
};

const propTypes = {
	className: PropTypes.string,
	dataAnalyticsId: PropTypes.string,
	dataAnalyticsIdSecondary: PropTypes.string,
	dataTestId: PropTypes.string,
	image: PropTypes.shape({
		sources: PropTypes.arrayOf(
			PropTypes.shape({
				srcSet: PropTypes.string,
				type: PropTypes.string,
			}),
		),
		fallbackSource: PropTypes.string,
		isLazy: PropTypes.bool,
		size: PropTypes.oneOf(['small', 'large']),
	}),
	link: PropTypes.shape({
		href: PropTypes.string,
		onClick: PropTypes.func,
		text: PropTypes.string,
	}),
	lLayout: PropTypes.oneOf(['v1', 'v2']),
	secondaryLink: PropTypes.shape({
		href: PropTypes.string,
		onClick: PropTypes.func,
		text: PropTypes.string,
	}),
	smallVariant: PropTypes.bool,
	textContent: PropTypes.shape({
		addBrandColour: PropTypes.oneOf(['heading', 'representative', 'subHeading']),
		heading: PropTypes.string,
		headingTag: PropTypes.string,
		legalText: PropTypes.string,
		paragraph: PropTypes.string,
		representative: PropTypes.string,
		subHeading: PropTypes.string,
		subHeadingTag: PropTypes.string,
		switchSubHeadingPosition: PropTypes.bool,
	}),
	variant: PropTypes.oneOf([
		'brandLight',
		'brandLightWithBorder',
		'whiteWithBorder',
		'grey',
		'brand',
		'black',
	]),
};

function ContentBanner1({
	className,
	dataAnalyticsId,
	dataAnalyticsIdSecondary,
	dataTestId,
	image,
	link,
	lLayout,
	secondaryLink,
	smallVariant,
	textContent,
	variant,
	...props
}) {
	const {
		headingTag,
		heading,
		subHeading,
		subHeadingTag,
		paragraph,
		representative,
		addBrandColour,
		switchSubHeadingPosition,
		legalText,
	} = textContent;

	const SubHeadingElement = (
		<StyledSubHeading
			size={{ xxs: smallVariant ? '1' : '3', l: '3' }}
			tag={subHeadingTag}
			type="heading"
			variant={variant}
			addBrandColour={addBrandColour}
			smallVariant={smallVariant}
		>
			{subHeading}
		</StyledSubHeading>
	);

	const paragraphSize = useMemo(() => ({ xxs: '1', l: lLayout === 'v1' ? '2' : '1' }), [lLayout]);

	const ParagraphElement = (
		<StyledParagraph size={paragraphSize} tag="p" type="paragraph" variant={variant}>
			{paragraph}
		</StyledParagraph>
	);

	const activePrimaryCTA = link.text && (!!link.href || !!link.onClick);
	const activeSecondaryCTA =
		secondaryLink.text && (!!secondaryLink.href || !!secondaryLink.onClick);
	const isPrimaryLink = !!link.href;
	const onlyOneCTA = !activeSecondaryCTA;

	// Refs
	const copyWrapperRef = useRef();
	const buttonsContainerRef = useRef();
	const primaryButtonRef = useRef();
	const secondaryButtonRef = useRef();
	/**
	 * Function that sets the layout of the buttons to side by side or stacked depending on the buttons width, gap, container size and viewport
	 */
	const setButtonsLayout = useCallback(() => {
		// Button gap set to initial side by side gap of --spacing-global-space6 = 16px
		const buttonGap = 16;
		const copyWrapperWidth = Math.floor(copyWrapperRef.current.offsetWidth) - 8; // Round down and adding some margins to account for button borders + extra spacing when total button widths calculation is close to copy wrapper width
		const primaryButtonClone = primaryButtonRef.current.cloneNode(true);
		const secondaryButtonClone = secondaryButtonRef.current.cloneNode(true);

		/**
		 * Calculate actual widths of buttons by setting width to auto from 100% of container when stacked
		 * Using cloned buttons for calculation removes chance of 'jank' as its not visible when appended and removed from DOM due to style set
		 */
		Object.assign(primaryButtonClone.style, {
			width: 'auto',
			position: 'absolute',
			visibility: 'hidden',
		});

		Object.assign(secondaryButtonClone.style, {
			width: 'auto',
			position: 'absolute',
			visibility: 'hidden',
		});

		buttonsContainerRef.current.appendChild(primaryButtonClone);
		buttonsContainerRef.current.appendChild(secondaryButtonClone);

		// Get primary and secondary button widths
		const primaryButtonWidth = primaryButtonClone.offsetWidth;
		const secondaryButtonWidth = secondaryButtonClone.offsetWidth;
		const totalButtonsWidth = Math.ceil(primaryButtonWidth + buttonGap + secondaryButtonWidth); // Round up value so to mitigate edge cases

		buttonsContainerRef?.current.removeChild(primaryButtonClone);
		buttonsContainerRef?.current.removeChild(secondaryButtonClone);

		// Update button styles to stacked or side by side based on total button widths
		if (
			totalButtonsWidth >= copyWrapperWidth &&
			!buttonsContainerRef.current.classList.contains('fuse-content-banner-1__buttons--stacked')
		) {
			buttonsContainerRef.current.classList.add('fuse-content-banner-1__buttons--stacked');
			primaryButtonRef.current.classList.add('fuse-content-banner-1__primary-cta--stacked');
			secondaryButtonRef.current.classList.add('fuse-content-banner-1__secondary-cta--stacked');
		}

		if (
			totalButtonsWidth < copyWrapperWidth &&
			buttonsContainerRef.current.classList.contains('fuse-content-banner-1__buttons--stacked')
		) {
			buttonsContainerRef.current.classList.remove('fuse-content-banner-1__buttons--stacked');
			primaryButtonRef.current.classList.remove('fuse-content-banner-1__primary-cta--stacked');
			secondaryButtonRef.current.classList.remove('fuse-content-banner-1__secondary-cta--stacked');
		}
	}, [link.text, secondaryLink.text]);

	/**
	 * Add resize event listener (set buttons layout) to window for when user changes orientation or viewport size
	 */
	useEffect(() => {
		if (activePrimaryCTA && activeSecondaryCTA) {
			const handleResize = Debounce(() => {
				setButtonsLayout();
			}, 200);

			window.addEventListener('resize', handleResize);

			return () => {
				window.removeEventListener('resize', handleResize);
			};
		}
		return () => {};
	}, []);

	/**
	 * Using useLayoutEffect to remove 'jank' of button growing to the available space when stacked ie execute effects first before rendering
	 */
	useLayoutEffect(() => {
		if (activePrimaryCTA && activeSecondaryCTA) setButtonsLayout();
	}, [link.text, secondaryLink.text]);

	const wrapApr = (aprText) => {
		const aprPattern = /(\d+(\.\d+)?% [Aa][Pp][Rr])/;
		const match = aprText.match(aprPattern);

		if (match) {
			const [fullMatch] = match;
			const parts = aprText.split(fullMatch);
			return (
				<>
					{parts[0]}
					<span className="fuse-content-banner-1__wrap-apr">{fullMatch}</span>
					{parts[1]}
				</>
			);
		}
		return aprText;
	};

	return (
		<StyledContentBanner
			as={onlyOneCTA && (isPrimaryLink ? 'a' : 'button')}
			href={link.href}
			variant={variant}
			image={image.fallbackSource}
			className={className}
			data-analyticsid={activePrimaryCTA && !activeSecondaryCTA && dataAnalyticsId}
			data-testid={dataTestId || 'fuse-content-banner-1-test-id'}
			onClick={!link.href && !activeSecondaryCTA ? link.onClick : undefined}
			addBrandColour={addBrandColour}
			lLayout={lLayout}
			smallVariant={smallVariant}
			{...props}
		>
			{image.fallbackSource && (
				<StyledPicture
					imageSize={smallVariant ? 'small' : image.size || 'small'}
					lLayout={lLayout}
					smallVariant={smallVariant}
				>
					<Picture {...image} alt="" />
				</StyledPicture>
			)}
			<StyledContent lLayout={lLayout}>
				<StyledText ref={copyWrapperRef}>
					{heading && (
						<StyledHeading
							size={{ xxs: smallVariant ? '2' : '5', l: '5' }}
							tag={headingTag}
							type="display"
							variant={variant}
							addBrandColour={addBrandColour}
							smallVariant={smallVariant}
						>
							{heading}
						</StyledHeading>
					)}
					{switchSubHeadingPosition ? (
						<>
							{paragraph && ParagraphElement}
							{subHeading && SubHeadingElement}
						</>
					) : (
						<>
							{subHeading && SubHeadingElement}
							{paragraph && ParagraphElement}
						</>
					)}
					{representative && (
						<StyledRepresentative
							size={{ xxs: smallVariant ? '2' : '5', l: '5' }}
							tag="p"
							type="display"
							variant={variant}
							addBrandColour={addBrandColour}
							smallVariant={smallVariant}
						>
							{wrapApr(representative)}
						</StyledRepresentative>
					)}
					{legalText && (
						<StyledParagraph
							size={{ xxs: 'm2', m: smallVariant ? 'm2' : 'm1', l: 'm1' }}
							tag="p"
							type="paragraph"
							variant={variant}
							data-testid="fuse-content-banner-1-legal-text-test-id"
							className="fuse-content-banner-1__legal-text"
						>
							{legalText}
						</StyledParagraph>
					)}
				</StyledText>

				{activePrimaryCTA && (
					<StyledButtons lLayout={lLayout} ref={buttonsContainerRef}>
						{activeSecondaryCTA ? (
							<>
								<Button
									className="fuse-content-banner-1__primary-cta"
									href={link.href}
									icon={<IconChevronRight />}
									iconRight
									variant="tertiary"
									data-analyticsid={dataAnalyticsId}
									onClick={link.onClick}
									ref={primaryButtonRef}
									size={{ xxs: smallVariant ? 'small' : 'large', l: 'large' }}
								>
									{link.text}
								</Button>
								<Button
									className="fuse-content-banner-1__secondary-cta"
									href={secondaryLink.href}
									icon={<IconChevronRight />}
									iconRight
									variant="tertiary"
									data-analyticsid={dataAnalyticsIdSecondary}
									onClick={secondaryLink.onClick}
									ref={secondaryButtonRef}
									size={{ xxs: smallVariant ? 'small' : 'large', l: 'large' }}
								>
									{secondaryLink.text}
								</Button>
							</>
						) : (
							<SpanButton
								className="fuse-content-banner-1__primary-span-cta"
								icon={<IconChevronRight />}
								iconRight
								variant="tertiary"
								size={{ xxs: smallVariant ? 'small' : 'large', l: 'large' }}
							>
								{link.text}
							</SpanButton>
						)}
					</StyledButtons>
				)}
			</StyledContent>
		</StyledContentBanner>
	);
}

ContentBanner1.propTypes = propTypes;
ContentBanner1.defaultProps = defaultProps;
ContentBanner1.displayName = 'ContentBanner1';

export default ContentBanner1;
